class KexSamplerProduct {
  constructor(root) {
    this.root = root;
    this.variants = this.readVariants();
    this.variantById = new Map(this.variants.map((variant) => [String(variant.id), variant]));
    this.cards = Array.from(this.root.querySelectorAll('[data-kex-sampler-variant-card]'));
    this.variantInput = this.root.querySelector('[data-kex-sampler-variant-id]');
    this.priceEl = this.root.querySelector('[data-kex-sampler-price]');
    this.comparePriceEl = this.root.querySelector('[data-kex-sampler-compare-price]');
    this.imageEl = this.root.querySelector('[data-kex-sampler-image]');
    this.selectedTitleEls = Array.from(this.root.querySelectorAll('[data-kex-sampler-selected-title]'));
    this.dropdown = this.root.querySelector('[data-kex-sampler-dropdown]');
    this.descriptionEl = this.root.querySelector('[data-kex-sampler-short-description]');
    this.includesEl = this.root.querySelector('[data-kex-sampler-includes]');
    this.includesContentEl = this.root.querySelector('[data-kex-sampler-includes-content]');
    this.statusEl = this.root.querySelector('[data-kex-sampler-status]');
    this.skuEl = this.root.querySelector('[data-kex-sampler-sku]');
    this.addButton = this.root.querySelector('[data-kex-sampler-atc]');
    this.liveRegion = this.root.querySelector('[data-kex-sampler-live]');

    const initialVariant = this.findInitialVariant();
    this.bindEvents();

    if (initialVariant) {
      this.selectVariant(initialVariant.id, { updateUrl: false, animate: false });
    }
  }

  readVariants() {
    const dataEl = this.root.querySelector('[data-kex-sampler-variants]');
    if (!dataEl) return [];

    try {
      const parsed = JSON.parse(dataEl.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('KEX Sampler: no se pudo leer el JSON de variantes.', error);
      return [];
    }
  }

  bindEvents() {
    this.cards.forEach((card) => {
      card.addEventListener('click', () => {
        this.selectVariant(card.dataset.variantId);
      });
    });
  }

  findInitialVariant() {
    const params = new URLSearchParams(window.location.search);
    const variantFromUrl = params.get('variant');

    if (variantFromUrl && this.variantById.has(String(variantFromUrl))) {
      return this.variantById.get(String(variantFromUrl));
    }

    const selectedId = this.variantInput ? this.variantInput.value : null;
    if (selectedId && this.variantById.has(String(selectedId))) {
      return this.variantById.get(String(selectedId));
    }

    return this.variants.find((variant) => variant.available) || this.variants[0];
  }

  selectVariant(variantId, options = {}) {
    const { updateUrl = true, animate = true } = options;
    const variant = this.variantById.get(String(variantId));
    if (!variant) return;

    this.selectedVariant = variant;
    this.updateCards(variant);
    this.updateForm(variant);
    this.updatePrice(variant);
    this.updateCopy(variant);
    this.updateImage(variant, animate);
    this.updateColors(variant);
    this.updateAvailability(variant);
    this.closeDropdown();

    if (updateUrl) this.updateUrl(variant);
    this.dispatchVariantChange(variant);
    this.announceVariant(variant);
  }

  updateCards(variant) {
    this.cards.forEach((card) => {
      const active = card.dataset.variantId === String(variant.id);
      card.classList.toggle('kex-sampler__variant-card--active', active);
      card.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  updateForm(variant) {
    if (this.variantInput) this.variantInput.value = variant.id;

    const productForm = this.root.querySelector('product-form');
    if (productForm) productForm.dataset.variantId = variant.id;
  }

  updatePrice(variant) {
    if (this.priceEl) this.priceEl.textContent = variant.price_formatted || '';

    if (!this.comparePriceEl) return;

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      this.comparePriceEl.textContent = variant.compare_at_price_formatted || '';
      this.comparePriceEl.hidden = false;
    } else {
      this.comparePriceEl.textContent = '';
      this.comparePriceEl.hidden = true;
    }
  }

  updateCopy(variant) {
    this.selectedTitleEls.forEach((selectedTitleEl) => {
      selectedTitleEl.textContent = variant.title || '';
    });

    if (this.descriptionEl) {
      this.descriptionEl.textContent = variant.sampler_short_description || '';
      this.descriptionEl.hidden = !variant.sampler_short_description;
    }

    if (this.skuEl) {
      this.skuEl.textContent = variant.sku ? `SKU: ${variant.sku}` : '';
      this.skuEl.hidden = !variant.sku;
    }

    this.updateIncludes(variant);
  }

  updateIncludes(variant) {
    if (!this.includesEl || !this.includesContentEl) return;

    const nextHtml = variant.sampler_includes || '';
    this.includesEl.hidden = !nextHtml;

    if (!nextHtml) {
      this.includesContentEl.innerHTML = '';
      return;
    }

    this.includesEl.classList.add('is-changing');
    window.setTimeout(() => {
      this.includesContentEl.innerHTML = nextHtml;
      this.includesEl.hidden = false;
      this.includesEl.classList.remove('is-changing');
    }, 120);
  }

  updateImage(variant, animate) {
    if (!this.imageEl || !variant.image || !variant.image.url) return;

    const applyImage = () => {
      this.imageEl.src = variant.image.url;
      this.imageEl.alt = variant.image.alt || variant.title || '';
      if (variant.image.srcset) this.imageEl.srcset = variant.image.srcset;
      this.imageEl.classList.remove('is-changing');
    };

    if (!animate) {
      applyImage();
      return;
    }

    this.imageEl.classList.add('is-changing');
    window.setTimeout(applyImage, 120);
  }

  closeDropdown() {
    if (this.dropdown) this.dropdown.open = false;
  }

  updateColors(variant) {
    if (variant.sampler_theme_color) {
      this.root.style.setProperty('--sampler-bg', variant.sampler_theme_color);
    }

    if (variant.sampler_accent_color) {
      this.root.style.setProperty('--sampler-accent', variant.sampler_accent_color);
    }

    const heroTone = KexSamplerProduct.getReadableHeroTone(variant.sampler_theme_color);
    this.root.style.setProperty('--sampler-hero-text', heroTone.text);
    this.root.style.setProperty('--sampler-hero-muted', heroTone.muted);
  }

  dispatchVariantChange(variant) {
    this.root.dispatchEvent(new CustomEvent('on:variant:change', {
      bubbles: true,
      detail: { variant }
    }));

    window.dispatchEvent(new CustomEvent('kex-sampler:variant-change', {
      detail: { variant }
    }));
  }

  updateAvailability(variant) {
    const availableText = variant.available ? 'Disponible' : 'Agotado';

    if (this.statusEl) {
      this.statusEl.textContent = availableText;
      this.statusEl.dataset.available = variant.available ? 'true' : 'false';
    }

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      this.addButton.textContent = variant.available
        ? this.addButton.dataset.availableText
        : this.addButton.dataset.soldOutText;
    }
  }

  updateUrl(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  announceVariant(variant) {
    if (!this.liveRegion) return;

    const status = variant.available ? 'disponible' : 'agotado';
    this.liveRegion.textContent = `${variant.title}, ${variant.price_formatted}, ${status}`;
  }

  static updateText(element, text) {
    if (!element) return;

    element.textContent = text;
    element.hidden = !text;
  }

  static itemMatchesVariant(item, variant) {
    const filter = item.dataset.variantFilter || '';
    if (!filter.trim()) return true;

    const needles = filter
      .split(/[,|\n]/)
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);

    if (!needles.length) return true;

    const variantValues = [
      String(variant.id || '').toLowerCase(),
      String(variant.title || '').toLowerCase(),
      ...(Array.isArray(variant.options) ? variant.options.map((option) => String(option || '').toLowerCase()) : [])
    ];

    return needles.some((needle) => variantValues.includes(needle));
  }

  static getReadableHeroTone(color) {
    const rgb = KexSamplerProduct.hexToRgb(color);
    if (!rgb) {
      return {
        text: '#ffffff',
        muted: 'rgba(255, 255, 255, 0.88)'
      };
    }

    const luminance = ((rgb.r * 299) + (rgb.g * 587) + (rgb.b * 114)) / 1000;
    if (luminance > 185) {
      return {
        text: '#08204a',
        muted: 'rgba(8, 32, 74, 0.78)'
      };
    }

    return {
      text: '#ffffff',
      muted: 'rgba(255, 255, 255, 0.88)'
    };
  }

  static hexToRgb(color) {
    if (!color || typeof color !== 'string') return null;

    const normalized = color.trim().replace('#', '');
    if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(normalized)) return null;

    const hex = normalized.length === 3
      ? normalized.split('').map((char) => char + char).join('')
      : normalized;

    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16)
    };
  }
}

class KexSamplerDetailPack {
  constructor(root) {
    this.root = root;
    this.variants = this.readVariants();
    this.variantById = new Map(this.variants.map((variant) => [String(variant.id), variant]));
    this.headingEl = this.root.querySelector('[data-kex-sampler-detail-heading]');
    this.kickerEl = this.root.querySelector('[data-kex-sampler-detail-kicker]');
    this.textEl = this.root.querySelector('[data-kex-sampler-detail-text]');
    this.detailItems = Array.from(this.root.querySelectorAll('[data-kex-sampler-detail-item]'));

    this.bindEvents();

    const initialVariant = this.findInitialVariant();
    if (initialVariant) this.updateDetail(initialVariant);
  }

  readVariants() {
    const dataEl = this.root.querySelector('[data-kex-sampler-detail-variants]');
    if (!dataEl) return [];

    try {
      const parsed = JSON.parse(dataEl.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('KEX Sampler detail: no se pudo leer el JSON de variantes.', error);
      return [];
    }
  }

  bindEvents() {
    window.addEventListener('kex-sampler:variant-change', (event) => {
      const eventVariant = event.detail && event.detail.variant;
      if (!eventVariant) return;

      const localVariant = this.variantById.get(String(eventVariant.id)) || {};
      const variant = {
        ...localVariant,
        ...eventVariant,
        sampler_detail_color: localVariant.sampler_detail_color || eventVariant.sampler_detail_color,
        sampler_detail_heading: localVariant.sampler_detail_heading || eventVariant.sampler_detail_heading,
        sampler_detail_kicker: localVariant.sampler_detail_kicker || eventVariant.sampler_detail_kicker,
        sampler_detail_text: localVariant.sampler_detail_text || eventVariant.sampler_detail_text
      };
      this.updateDetail(variant);
    });
  }

  findInitialVariant() {
    const params = new URLSearchParams(window.location.search);
    const variantFromUrl = params.get('variant');

    if (variantFromUrl && this.variantById.has(String(variantFromUrl))) {
      return this.variantById.get(String(variantFromUrl));
    }

    return this.variants[0];
  }

  updateDetail(variant) {
    if (variant.sampler_theme_color) {
      this.root.style.setProperty('--sampler-pre-bg', variant.sampler_theme_color);
    }

    if (variant.sampler_detail_color) {
      this.root.style.setProperty('--sampler-detail-bg', variant.sampler_detail_color);
    }

    KexSamplerProduct.updateText(this.headingEl, variant.sampler_detail_heading || '');
    KexSamplerProduct.updateText(this.kickerEl, variant.sampler_detail_kicker || '');
    KexSamplerProduct.updateText(this.textEl, variant.sampler_detail_text || '');

    this.detailItems.forEach((item) => {
      item.hidden = !KexSamplerProduct.itemMatchesVariant(item, variant);
    });
  }
}

window.KexSamplerProduct = KexSamplerProduct;
window.KexSamplerDetailPack = KexSamplerDetailPack;

function initKexSamplerSections() {
  document.querySelectorAll('[data-kex-sampler-detail-section]').forEach((root) => {
    if (root.dataset.kexSamplerDetailInitialized === 'true') return;
    root.dataset.kexSamplerDetailInitialized = 'true';
    new KexSamplerDetailPack(root);
  });

  document.querySelectorAll('[data-kex-sampler]').forEach((root) => {
    if (root.dataset.kexSamplerInitialized === 'true') return;
    root.dataset.kexSamplerInitialized = 'true';
    new KexSamplerProduct(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKexSamplerSections);
} else {
  initKexSamplerSections();
}

document.addEventListener('shopify:section:load', initKexSamplerSections);
